"use client";

import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Alert,
  Form,
  InputGroup,
  Button,
  Badge,
  Nav,
  OverlayTrigger,
  Tooltip,
  Modal,
  ButtonGroup,
} from "react-bootstrap";
import axios from "axios";
import NavBar from "./Navbar";

const API_BASE = "http://109.75.164.217:84/api";
const SETTINGS_ENDPOINT = `${API_BASE}/Settings`;
const APP_SETTINGS_CRUD = `${SETTINGS_ENDPOINT}/appsettings`;
const TABLEFIELDS_CRUD = `${SETTINGS_ENDPOINT}/tablefields`;

// Safely turn an ISO string (or Date) into a "YYYY-MM-DDThh:mm" value,
// or return empty string if invalid.
const toDateTimeLocal = (dateInput) => {
  if (!dateInput) return "";
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 16);
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    return new Date(dateString).toLocaleString();
  } catch {
    return dateString;
  }
};

const exportToCSV = (data, filename = "export") => {
  if (!data || data.length === 0) {
    alert("No data to export");
    return;
  }
  const headers = Array.from(new Set(data.flatMap((item) => Object.keys(item))));
  let csv = headers.join(",") + "\n";
  data.forEach((item) => {
    const row = headers.map((h) => {
      const v = item[h] ?? "";
      return typeof v === "string" && v.includes(",") ? `"${v}"` : v;
    });
    csv += row.join(",") + "\n";
  });
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export default function SettingsPage({ user, onLogout }) {
  const [activePanel, setActivePanel] = useState("app");
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [current, setCurrent] = useState({});

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await axios.get(SETTINGS_ENDPOINT);
      const payload = resp.data || {};
      const arr =
        activePanel === "app"
          ? payload.appSettings || []
          : payload.tableFields || [];
      setData(Array.isArray(arr) ? arr : []);
    } catch (e) {
      console.error(e);
      setError(
        `Failed to load ${
          activePanel === "app" ? "app settings" : "table fields"
        }: ${e.message}`
      );
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activePanel]);

  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return data;
    const q = searchQuery.toLowerCase();
    return data.filter((item) =>
      Object.values(item).some(
        (val) => val != null && val.toString().toLowerCase().includes(q)
      )
    );
  }, [data, searchQuery]);

  const openAdd = () => {
    setModalMode("add");
    if (activePanel === "app") {
      setCurrent({
        id: 0,
        settingCode: "",
        settingValue: "",
        sysUser: "",
        timeStamp: new Date().toISOString(),
        obj: "",
        settingName: "",
      });
    } else {
      setCurrent({
        id: 0,
        obj: "",
        field: "",
        fieldOrder: 0,
        fieldIsFixed: 0,
        fieldIsActive: 0,
        sysUser: "",
        statusDate: new Date().toISOString(),
        timeStamp: new Date().toISOString(),
        fieldUsedTotalhours: 0,
      });
    }
    setShowModal(true);
  };

  const openEdit = (row) => {
    setModalMode("edit");
    setCurrent({ ...row });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const payload = [current];
      if (activePanel === "app") {
        if (modalMode === "add") {
          await axios.post(APP_SETTINGS_CRUD, payload);
        } else {
          await axios.put(APP_SETTINGS_CRUD, payload);
        }
      } else {
        if (modalMode === "add") {
          await axios.post(TABLEFIELDS_CRUD, payload);
        } else {
          await axios.put(TABLEFIELDS_CRUD, payload);
        }
      }
      setShowModal(false);
      fetchData();
    } catch (e) {
      alert(`Save failed: ${e.message}`);
    }
  };

  const LoadingSkeleton = () => (
    <>
      {[...Array(5)].map((_, i) => (
        <tr key={i}>
          {[...Array(activePanel === "app" ? 8 : 11)].map((_, j) => (
            <td key={j}>
              <div className="placeholder-glow">
                <span className="placeholder col-12"></span>
              </div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );

  return (
    <Container fluid>
      <NavBar user={user} onLogout={onLogout} />
      <Row className="g-4">
        <Col md={3} lg={2}>
          <Card className="shadow-sm border-0 mb-4">
            <Card.Body className="p-0">
              <Nav className="flex-column">
                <Nav.Link
                  className={`px-4 py-3 border-start border-4 ${
                    activePanel === "app" ? "border-primary" : "border-white"
                  }`}
                  active={activePanel === "app"}
                  onClick={() => setActivePanel("app")}
                >
                  <i className="bi bi-gear-fill me-2" />
                  App Settings
                </Nav.Link>
                <Nav.Link
                  className={`px-4 py-3 border-start border-4 ${
                    activePanel === "config" ? "border-primary" : "border-white"
                  }`}
                  active={activePanel === "config"}
                  onClick={() => setActivePanel("config")}
                >
                  <i className="bi bi-sliders me-2" />
                  Table Fields
                </Nav.Link>
              </Nav>
            </Card.Body>
          </Card>
        </Col>

        <Col md={9} lg={10}>
          <Card className="shadow-sm border-0">
          <Card.Header className="bg-white py-2">
  <div className="d-flex align-items-center">
    <h5 className="mb-0 me-auto">
      {activePanel === "app" ? "Application Settings" : "Configured Table Fields"}
    </h5>

    <Badge bg="light" text="dark" className="me-3">
      {filteredData.length} items
    </Badge>

    <InputGroup size="sm" className="me-2">
      <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
      <Form.Control
        placeholder={ activePanel==="app" ? "Search settings…" : "Search fields…" }
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
      />
      {searchQuery && (
        <Button variant="outline-secondary" onClick={() => setSearchQuery("")}>
          <i className="bi bi-x" />
        </Button>
      )}
    </InputGroup>

    <ButtonGroup size="sm">
      <Button
        variant="outline-primary"
        onClick={fetchData}
        title="Refresh"
      >
        <i className="bi bi-arrow-clockwise" />
      </Button>
      <Button
        variant="outline-success"
        onClick={openAdd}
        title={ activePanel==="app" ? "Add Setting" : "Add Field" }
      >
        <i className="bi bi-plus-circle" />
      </Button>
      <Button
        variant="outline-success"
        onClick={() => exportToCSV(filteredData, activePanel==="app"?"app-settings":"table-fields")}
        disabled={!filteredData.length}
        title="Export"
      >
        <i className="bi bi-file-earmark-excel" />
      </Button>
    </ButtonGroup>
  </div>
</Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="danger">
                  <i className="bi bi-exclamation-triangle-fill me-2" />
                  {error}
                </Alert>
              )}
              {!error && (
                <div
                  style={{ maxHeight: "70vh", overflowY: "auto" }}
                  className="border rounded"
                >
                  <Table striped hover responsive className="mb-0">
                    <thead className="sticky-top bg-white">
                      <tr>
                       
                        {activePanel === "app" ? (
                          <>
                            <th>Code</th>
                            <th>Value</th>
                           
                            <th>Object</th>
                            <th>Name</th>
                          </>
                        ) : (
                          <>
                            <th>Object</th>
                            <th>Field</th>
                         
                            <th>Fixed?</th>
                            <th>Active?</th>
                         
                          </>
                        )}
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <LoadingSkeleton />
                      ) : filteredData.length === 0 ? (
                        <tr>
                          <td
                            colSpan={activePanel === "app" ? 8 : 11}
                            className="text-center py-5"
                          >
                            <i className="bi bi-inbox fs-1 text-muted mb-2" />
                            {searchQuery
                              ? "No items match your search"
                              : "No items found"}
                          </td>
                        </tr>
                      ) : (
                        filteredData.map((row) => (
                          <tr key={row.id}>
                           
                            {activePanel === "app" ? (
                              <>
                                <td>
                                 {row.settingCode}
                                </td>
                                <td>
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip>{row.settingValue}</Tooltip>}
                                  >
                                    <div
                                      style={{
                                        maxWidth: "200px",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {row.settingValue}
                                    </div>
                                  </OverlayTrigger>
                                </td>
                               
                               
                                <td>{row.obj}</td>
                                <td>{row.settingName}</td>
                              </>
                            ) : (
                              <>
                                <td>{row.obj}</td>
                                <td>{row.field}</td>
                                
                                <td>{row.fieldIsFixed}</td>
                                <td>{row.fieldIsActive}</td>
                               
                               
                              </>
                            )}
                            <td>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => openEdit(row)}
                              >
                                Edit
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === "add"
              ? activePanel === "app"
                ? "Add New Setting"
                : "Add New Field"
              : activePanel === "app"
              ? "Edit Setting"
              : "Edit Field"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {activePanel === "app" ? (
              <>
                <Form.Group className="mb-2">
                  <Form.Label>Code</Form.Label>
                  <Form.Control
                    value={current.settingCode}
                    onChange={(e) =>
                      setCurrent({ ...current, settingCode: e.target.value })
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Value</Form.Label>
                  <Form.Control
                    value={current.settingValue}
                    onChange={(e) =>
                      setCurrent({ ...current, settingValue: e.target.value })
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>User</Form.Label>
                  <Form.Control
                    value={current.sysUser}
                    onChange={(e) =>
                      setCurrent({ ...current, sysUser: e.target.value })
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Timestamp</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={toDateTimeLocal(current.timeStamp)}
                    onChange={(e) =>
                      setCurrent({
                        ...current,
                        timeStamp: new Date(e.target.value).toISOString(),
                      })
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Object</Form.Label>
                  <Form.Control
                    value={current.obj}
                    onChange={(e) =>
                      setCurrent({ ...current, obj: e.target.value })
                    }
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    value={current.settingName}
                    onChange={(e) =>
                      setCurrent({ ...current, settingName: e.target.value })
                    }
                  />
                </Form.Group>
              </>
            ) : (
              <>
                <Form.Group className="mb-2">
                  <Form.Label>Object</Form.Label>
                  <Form.Control
                    value={current.obj}
                    onChange={(e) =>
                      setCurrent({ ...current, obj: e.target.value })
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Field</Form.Label>
                  <Form.Control
                    value={current.field}
                    onChange={(e) =>
                      setCurrent({ ...current, field: e.target.value })
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Order</Form.Label>
                  <Form.Control
                    type="number"
                    value={current.fieldOrder}
                    onChange={(e) =>
                      setCurrent({
                        ...current,
                        fieldOrder: parseInt(e.target.value, 10) || 0,
                      })
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Fixed?</Form.Label>
                  <Form.Control
                    type="number"
                    value={current.fieldIsFixed}
                    onChange={(e) =>
                      setCurrent({
                        ...current,
                        fieldIsFixed: parseInt(e.target.value, 10) || 0,
                      })
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Active?</Form.Label>
                  <Form.Control
                    type="number"
                    value={current.fieldIsActive}
                    onChange={(e) =>
                      setCurrent({
                        ...current,
                        fieldIsActive: parseInt(e.target.value, 10) || 0,
                      })
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>User</Form.Label>
                  <Form.Control
                    value={current.sysUser}
                    onChange={(e) =>
                      setCurrent({ ...current, sysUser: e.target.value })
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Status Date</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={toDateTimeLocal(current.statusDate)}
                    onChange={(e) =>
                      setCurrent({
                        ...current,
                        statusDate: new Date(e.target.value).toISOString(),
                      })
                    }
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Timestamp</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={toDateTimeLocal(current.timeStamp)}
                    onChange={(e) =>
                      setCurrent({
                        ...current,
                        timeStamp: new Date(e.target.value).toISOString(),
                      })
                    }
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Used Hours</Form.Label>
                  <Form.Control
                    type="number"
                    value={current.fieldUsedTotalhours}
                    onChange={(e) =>
                      setCurrent({
                        ...current,
                        fieldUsedTotalhours: parseInt(e.target.value, 10) || 0,
                      })
                    }
                  />
                </Form.Group>
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            {modalMode === "add"
              ? activePanel === "app"
                ? "Create"
                : "Add"
              : activePanel === "app"
              ? "Update"
              : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
